import { Activity, ArrowRight, Gem, Heart, Leaf, Sprout } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CHAPTERS } from "@/data/content";

const POINT_ICONS = [Leaf, Activity, Gem, Sprout];

export default function Manifesto() {
  return (
    <section id="story" className="bg-[#2C221E] text-[#FAF7F2] py-24 sm:py-36 relative overflow-hidden">
      <p className="absolute -top-8 left-1/2 -translate-x-1/2 font-script text-[22vw] leading-none text-outline select-none pointer-events-none whitespace-nowrap">
        almo
      </p>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C86D51] mb-6">Our Story</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Real ingredients. Real families.{" "}
              <em className="italic text-[#F8C8DC]">The sweetest memories.</em>
            </h2>
            <div className="mt-7 space-y-5 text-[#FAF7F2]/70 leading-relaxed max-w-xl">
              <p>
                It all began in June 2018, with a new mother reading ingredient labels and
                dreaming of better treats for her daughter — and a Sunday baking ritual that
                became a family tradition.
              </p>
            </div>
            <a
              href="/our-story"
              data-testid="read-full-story-link"
              className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#FAF7F2]/25 px-6 py-3 text-sm font-medium text-[#FAF7F2] hover:bg-[#F8C8DC] hover:text-[#6A2C3E] hover:border-[#F8C8DC] transition-all duration-300"
            >
              Read our full story <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative rounded-[2rem] overflow-hidden spotlight-frame lg:rotate-1 group">
              <img
                src="/assets/story-cake.png"
                alt="Freshly baked organic almond flour cake topped with toasted almond flakes"
                loading="lazy"
                className="w-full aspect-[6/5] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <span className="absolute left-5 bottom-5 inline-flex items-center gap-2 rounded-full bg-[#F8C8DC] text-[#6A2C3E] px-5 py-2.5 text-xs font-semibold shadow-lg">
                <Heart className="w-3.5 h-3.5 fill-current" /> Made with care, for your family
              </span>
            </div>
          </Reveal>
        </div>

        <div className="mt-20 sm:mt-28 flex flex-col gap-20 sm:gap-28">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} className={`grid lg:grid-cols-12 gap-8 lg:gap-14 items-center ${i % 2 ? "" : ""}`}>
              <div className={`lg:col-span-5 ${i % 2 ? "lg:order-2 lg:ml-16" : "lg:mr-16"}`}>
                <span className="font-mono text-sm tracking-[0.3em] text-[#C86D51]">{c.num}</span>
                <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 tracking-tight">
                  {c.title}
                </h3>
                {c.points ? (
                  <>
                    <p className="mt-3 font-serif italic text-lg text-[#F8C8DC]">{c.sub}</p>
                    <ul className="mt-6 space-y-4">
                      {c.points.map(([t, d], j) => {
                        const Icon = POINT_ICONS[j % POINT_ICONS.length];
                        return (
                          <li key={t} className="flex gap-3.5">
                            <span className="w-9 h-9 rounded-full bg-[#F8C8DC]/10 border border-[#F8C8DC]/25 flex items-center justify-center shrink-0">
                              <Icon className="w-4 h-4 text-[#F8C8DC]" />
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-[#FAF7F2]">{t}</p>
                              <p className="text-sm text-[#FAF7F2]/60 leading-relaxed mt-0.5">{d}</p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="mt-7 flex flex-wrap gap-2">
                      {c.badges.map((b) => (
                        <span key={b} className="rounded-full border border-[#FAF7F2]/20 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#FAF7F2]/70">
                          {b}
                        </span>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="mt-4 text-[#FAF7F2]/60 leading-relaxed">{c.text}</p>
                )}
              </div>
              <div className={`lg:col-span-7 ${i % 2 ? "lg:order-1" : ""} ${i % 2 ? "lg:-rotate-1" : "lg:rotate-1"}`}>
                <div className="rounded-[2rem] overflow-hidden spotlight-frame group">
                  <img
                    src={c.img}
                    alt={c.alt}
                    loading="lazy"
                    className="w-full aspect-[16/10] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
